import type { Constructor, Country, Driver, DriverKey, Rarity } from "../types";

// Typed factory keeps the field order in ONE place instead of at every call site.
// A driver's variants are keyed by the TEAM they drove for (name|team); the
// card's rarity is its "level" for that stint. No driver has two cards at the
// same team (see the engine.test.ts invariant).
function d(
  name: string,
  country: Country,
  team: Constructor,
  rarity: Rarity,
  pace: number,
  consistency: number,
  rain: number,
): Driver {
  return { type: "driver", name, country, team, rarity, pace, consistency, rain };
}

export const DRIVERS: readonly Driver[] = [
  // ── Mythic — the icons at the peak of their careers ──────────────────────
  d("Senna", "BRA", "McLaren", "mythic", 99, 82, 99), //     blistering pace & rain, least consistent
  d("Prost", "FRA", "McLaren", "mythic", 93, 96, 91), //     "the Professor" — most consistent
  d("Lauda", "AUT", "Ferrari", "mythic", 92, 95, 93), //     "the Computer" — clinical in any conditions
  d("Schumacher", "GER", "Ferrari", "mythic", 96, 94, 90),
  d("Hamilton", "GBR", "Mercedes", "mythic", 95, 93, 92),
  d("Fangio", "ARG", "Mercedes", "mythic", 97, 95, 92), //   five-time champion, masterful in any car
  d("Verstappen", "NED", "Red Bull", "mythic", 96, 87, 90), // ruthless all-rounder, four-time champion
  // ── Legend ────────────────────────────────────────────────────────────────
  d("Senna", "BRA", "Williams", "legend", 97, 76, 95), //    peak pace & rain, lower consistency
  d("Prost", "FRA", "Williams", "legend", 90, 95, 78), //    clinical, weaker in the wet
  d("Lauda", "AUT", "McLaren", "legend", 87, 93, 83), //     ruthlessly precise
  d("Schumacher", "GER", "Benetton", "legend", 93, 82, 88), // aggressive pace + rain genius
  d("Hamilton", "GBR", "McLaren", "legend", 91, 83, 90), //  electric pace, wet master, young edges
  d("Vettel", "GER", "Red Bull", "legend", 93, 89, 85), //   dominant, metronomic
  d("Hakkinen", "FIN", "McLaren", "legend", 90, 84, 83), //  flying Finn — pure pace
  d("Mansell", "GBR", "Williams", "legend", 92, 77, 82), //  brave & fast, occasionally wild
  d("Piquet", "BRA", "Williams", "legend", 88, 83, 81), //   crafty triple champion, 1987 title
  d("Alonso", "ESP", "Renault", "legend", 91, 88, 91), //    complete driver, rain magician
  d("Ascari", "ITA", "Ferrari", "legend", 95, 89, 86), //    imperious 1952–53 double champion
  d("G. Villeneuve", "CAN", "Ferrari", "legend", 90, 66, 97), // fearless #27, otherworldly in the wet
  d("Rindt", "AUT", "Lotus", "legend", 92, 82, 84), //       blistering 1970 posthumous champion
  d("Raikkonen", "FIN", "Ferrari", "legend", 88, 80, 82), // Iceman — 2007 champion, ice-cool
  d("Norris", "GBR", "McLaren", "legend", 90, 86, 84), //    fast, polished, assured
  d("Fittipaldi", "BRA", "Lotus", "legend", 88, 80, 82), //  smooth young champion
  d("Jones", "AUS", "Williams", "legend", 88, 81, 82), //    tough, fast 1980 champion
  d("Leclerc", "MON", "Ferrari", "legend", 91, 82, 88), //   Ferrari's #1 — imperious qualifier, superb in the wet
  d("Russell", "GBR", "Mercedes", "legend", 88, 85, 84), //  Mercedes lead, polished and consistent race-winner
  d("Ricciardo", "AUS", "Red Bull", "legend", 89, 82, 85), // peak-era honey badger, elite late-braker
  // ── Normal ────────────────────────────────────────────────────────────────
  d("Senna", "BRA", "Lotus", "normal", 88, 64, 86), //       young Senna — raw pace & rain, error-prone
  d("Schumacher", "GER", "Mercedes", "normal", 80, 76, 78), // washed — Mercedes
  d("Hamilton", "GBR", "Ferrari", "normal", 80, 77, 81), //  washed — Ferrari, rain touch endures
  d("Alonso", "ESP", "Ferrari", "normal", 84, 76, 79), //    complete & relentless in the wet
  d("Alonso", "ESP", "McLaren", "normal", 80, 79, 80), //    elite driver, uncompetitive McLaren era
  d("Barrichello", "BRA", "Ferrari", "normal", 81, 80, 75), // dependable wingman
  d("Barrichello", "BRA", "Brawn", "normal", 80, 79, 76), //  veteran steadiness
  d("Massa", "BRA", "Ferrari", "normal", 85, 79, 74), //     rapid, weaker in the wet
  d("Massa", "BRA", "Williams", "normal", 80, 77, 74), //    veteran swansong at Williams
  d("Webber", "AUS", "Red Bull", "normal", 82, 76, 79), //   gritty and quick
  d("Button", "GBR", "Brawn", "normal", 78, 78, 82), //      smooth, superb rain craft
  d("Button", "GBR", "McLaren", "normal", 80, 78, 80), //    McLaren race-winner
  d("Rosberg", "GER", "Mercedes", "normal", 82, 80, 74), //  cerebral qualifier, modest in wet
  d("Montoya", "COL", "Williams", "normal", 87, 72, 79), //  explosive pace, wild edges
  d("Coulthard", "GBR", "McLaren", "normal", 80, 78, 78), // solid and reliable
  d("de la Rosa", "ESP", "McLaren", "normal", 78, 79, 78), // dependable 2006 McLaren stand-in
  d("J. Villeneuve", "CAN", "Williams", "normal", 83, 74, 77), // fearless 1997 champion
  d("Bottas", "FIN", "Mercedes", "normal", 82, 80, 74), //   strong qualifier, steady #2
  d("Perez", "MEX", "Red Bull", "normal", 80, 74, 82), //    tyre whisperer, street & rain craft
  d("Tsunoda", "JPN", "Red Bull", "normal", 80, 74, 77), //  feisty and quick, still maturing
  d("Antonelli", "ITA", "Mercedes", "normal", 82, 74, 78), // fast-rising rookie, rough edges
  d("Alboreto", "ITA", "Ferrari", "normal", 83, 79, 77), //  1985 title challenger, smooth
  d("Trulli", "ITA", "Renault", "normal", 82, 79, 76), //    superb one-lap qualifier
  d("Patrese", "ITA", "Williams", "normal", 80, 78, 76), //  experienced and dependable
  d("Patrese", "ITA", "Benetton", "normal", 80, 79, 77), //  veteran leader at Benetton
  d("Hill", "GBR", "Williams", "normal", 82, 78, 79), //     determined, capable in the wet
  d("Berger", "AUT", "Ferrari", "normal", 82, 74, 78), //    fast and brave, inconsistent
  d("Irvine", "GBR", "Ferrari", "normal", 81, 75, 75), //    combative number two
  d("Piastri", "AUS", "McLaren", "normal", 81, 81, 77), //   ice-calm and consistent, title still to come
  d("Herbert", "GBR", "Benetton", "normal", 78, 77, 76), //  game racer, midfield class
  d("Fisichella", "ITA", "Renault", "normal", 79, 76, 77), // quick over one lap
  d("Reutemann", "ARG", "Williams", "normal", 80, 76, 80), // silky-smooth, mercurial
  d("Colapinto", "ARG", "Renault", "normal", 78, 74, 76), // Argentina's current hope, rapid but raw
  d("Albon", "THA", "Williams", "normal", 80, 79, 79), //    composed Williams lead, quietly quick
  d("Alesi", "FRA", "Ferrari", "normal", 80, 72, 86), //     flamboyant, wet ace, never a champion
  d("Gasly", "FRA", "Renault", "normal", 82, 78, 77), //     resilient midfield leader
  d("Ocon", "FRA", "Renault", "normal", 80, 78, 78), //      opportunist, surprise race-winner
  d("Sainz", "ESP", "Ferrari", "normal", 82, 79, 78), //     polished, assured race-winner
  d("P. Rodriguez", "MEX", "Ferrari", "normal", 80, 74, 82), // Pedro — brave, superb in the wet
  d("R. Rodriguez", "MEX", "Ferrari", "normal", 80, 72, 76), // Ricardo — precocious, raw, lost at 20
  d("Stroll", "CAN", "Williams", "normal", 78, 75, 80), //   raw, capable in the rain
  d("Peterson", "SWE", "Lotus", "normal", 88, 70, 81), //    SuperSwede — blistering pace, error-prone
  d("De Angelis", "ITA", "Lotus", "normal", 81, 80, 75), //  elegant, smooth and consistent
];

/** Resolve a driver by `${name}|${team}` key. Returns undefined if missing. */
export function findDriver(key: DriverKey): Driver | undefined {
  const [name, team] = key.split("|");
  return DRIVERS.find((dr) => dr.name === name && dr.team === team);
}

// Driver → constructors they historically drove for, used for synergy. Broader
// than the card set on purpose: a driver earns the historic-drive bonus on ANY
// of their real teams, not only the one their drafted card represents.
export const HIST_TEAM: Readonly<Record<string, readonly Constructor[]>> = {
  Senna: ["McLaren", "Lotus", "Williams"],
  Schumacher: ["Ferrari", "Benetton", "Mercedes"],
  Hamilton: ["Mercedes", "McLaren", "Ferrari"],
  Verstappen: ["Red Bull"],
  Prost: ["McLaren", "Ferrari", "Williams", "Renault"],
  Alonso: ["Renault", "Ferrari", "McLaren"],
  Vettel: ["Red Bull", "Ferrari"],
  Raikkonen: ["Ferrari", "McLaren", "Lotus"],
  Barrichello: ["Ferrari", "Williams", "Brawn"],
  Massa: ["Ferrari", "Williams"],
  Webber: ["Red Bull", "Williams"],
  Button: ["Brawn", "McLaren", "Williams", "Benetton"],
  Rosberg: ["Mercedes", "Williams"],
  Montoya: ["Williams", "McLaren"],
  Hakkinen: ["McLaren"],
  Mansell: ["Williams", "Ferrari", "McLaren"],
  Piquet: ["Williams", "Benetton", "Lotus"],
  Coulthard: ["McLaren", "Williams", "Red Bull"],
  "J. Villeneuve": ["Williams"],
  "G. Villeneuve": ["Ferrari"],
  Ascari: ["Ferrari"],
  Alboreto: ["Ferrari"],
  Trulli: ["Renault"],
  Antonelli: ["Mercedes"],
  Leclerc: ["Ferrari"],
  Russell: ["Mercedes", "Williams"],
  Ricciardo: ["Red Bull", "Renault", "McLaren"],
  Albon: ["Williams", "Red Bull"],
  Colapinto: ["Renault", "Williams"],
  Lauda: ["Ferrari", "McLaren"],
  Bottas: ["Mercedes", "Williams"],
  Perez: ["Red Bull", "McLaren"],
  Patrese: ["Williams", "Benetton"],
  Hill: ["Williams"],
  Berger: ["McLaren", "Ferrari", "Benetton"],
  Irvine: ["Ferrari"],
  Norris: ["McLaren"],
  Piastri: ["McLaren"],
  Fittipaldi: ["Lotus", "McLaren"],
  Herbert: ["Benetton"],
  Fisichella: ["Renault", "Benetton"],
  Jones: ["Williams"],
  Reutemann: ["Williams", "Ferrari"],
  Fangio: ["Mercedes", "Ferrari"],
  Alesi: ["Ferrari", "Benetton"],
  Gasly: ["Red Bull", "Renault"],
  Ocon: ["Renault"],
  Sainz: ["Ferrari", "Renault", "McLaren", "Williams"],
  "P. Rodriguez": ["Ferrari"],
  "R. Rodriguez": ["Ferrari"],
  Rindt: ["Lotus"],
  "de la Rosa": ["McLaren"],
  Tsunoda: ["Red Bull"],
  Stroll: ["Williams"],
  Peterson: ["Lotus"],
  "De Angelis": ["Lotus"],
};
